// R64 — WHAT THE POT SAYS WHEN IT IS EMPTY, AND WHAT IT SAID WHEN IT NEVER ARRIVED.
//
// **THE FAILURE STATE WAS NOT A MISSING SENTENCE BUT A WRONG ONE**, which is R60's Card Library
// finding word for word one room over. The panel read:
//
//     "The Cauldron is empty. From any working in this room, choose Send to Cauldron, then come
//      back to bring them together."
//
// — instructions to go and do the thing she already did, printed over a pot the room could not read.
// The Cauldron is where a member stacks what she is about to pay a generation to compile, so an
// empty one is not a cosmetic state: it is the compile button gone and the whole panel replaced by
// an invitation to start over.
//
// **THE DECISION IS FOUR-WAY AND IT USED TO BE A TWO-ARM TERNARY INSIDE A NINE-HOOK COMPONENT**, so
// nothing could run it. `items: null` is W5-2/R39's third state — NOT LOADED, distinct from LOADED
// AND EMPTY (R18's distinction, the one this house keeps paying for) — and `loaded` separates "still
// looking" from "looked, and could not read it".
//
//   !loaded          -> still looking
//   items === null   -> the read did not land. The note beside it says what the server said.
//   items.length 0   -> genuinely empty, and the invitation is the right thing to print
//   otherwise        -> her pot, the name field and the two actions
//
// Props in, tree out, no hook — so `scripts/r64-cauldron.mjs` CALLS it once per state and reads the
// tree back, which is the only way the sentence-over-a-pot-that-never-arrived defect is reachable
// without a browser and a broken database.
//
// **R69 PUT THE CLEAR'S TWO STATES IN HERE FOR THE SAME REASON.** Emptying the pot was a single tap
// on a text button beside the primary one, and it is the ONE destroyer on this site R35's
// biconditional could not reach: that fixture hunts by NAME (`delete`/`remove`), and `clearAll`
// arrives as `onClear={clearAll}` on a plain `onClick={onClear}`. It asks now, and the ask is a
// hookless DECISION on this side of the boundary while `clearAsk` is state on the other — which is
// the difference between a confirm a gate can run and R35's own, which only a browser can reach.
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { cauldronKindLabel } from "../magicRoom.js";
import { MAX_CREATION_NAME } from "../creationName.js";

export type CauldronItem = { id: string; kind: string; title: string; createdAt: string };

export function CauldronList({
  items, loaded, language, busy, creationName, t,
  onCreationName, onRemove, onCompile, onClear, clearAsk = false, onClearAsk,
}: {
  // NULL IS NOT AN EMPTY POT. It is the answer that did not land, and the whole round is that these
  // are two facts (R18) rather than one absent list.
  items: CauldronItem[] | null;
  loaded: boolean;
  language: string;
  busy: boolean;
  creationName: string;
  // The catalog reader is handed in, because this file may not import `strings.js` and still be
  // callable by the loader that RUNS it.
  t: (language: string, key: string, vars?: Record<string, unknown>) => string;
  onCreationName: (value: string) => void;
  onRemove: (id: string) => void;
  onCompile: () => void;
  onClear: () => void;
  // R69 — HAS SHE ASKED TO EMPTY THE POT? The two clear states are a DECISION and it lives here,
  // where a gate can call it, while the state that drives it stays in `CauldronExperience`. It takes
  // a DEFAULT because the ask is the addition and a caller that knows nothing about it must land on
  // the un-asked control rather than on the destructive one (a required prop would refuse the call
  // site instead, which is right when the missing prop is a capability and wrong when it is a mode).
  clearAsk?: boolean;
  onClearAsk?: (asking: boolean) => void;
}): ReactNode {
  if (!loaded) return <p className="find-your-type__status">{t(language, "cauldron.looking")}</p>;
  // THE READ DID NOT LAND. Never the invitation — she has already sent things here, and telling her
  // to go and send some is the one sentence that reads as her workings being gone.
  if (items === null) return <p className="cauldron-empty">{t(language, "cauldron.unread")}</p>;
  if (!items.length) return <p className="cauldron-empty">{t(language, "cauldron.empty")}</p>;
  return (
    <>
      <ul className="cauldron-items">
        {items.map((it) => (
          <li key={it.id} className="cauldron-item">
            {/* R50: never `|| it.kind`. The resolver is TOTAL, so an unrostered kind reads as a sentence, not a machine word. */}
            <span className="cauldron-item__kind">{cauldronKindLabel(it.kind)}</span>
            <span className="cauldron-item__title">{it.title}</span>
            <button type="button" className="cauldron-item__remove" onClick={() => onRemove(it.id)} aria-label={t(language, "cauldron.remove")}><X size={15} /></button>
          </li>
        ))}
      </ul>
      {/* N7-4: NAME YOUR CREATION. Optional, verbatim, and it titles the compiled working itself,
          not just the shelf row. */}
      <div className="creation-name creation-name--cauldron">
        <label className="creation-name__label" htmlFor="cauldron-creation-name">{t(language, "cauldron.name.label")}</label>
        <input id="cauldron-creation-name" className="creation-name__input" type="text" value={creationName}
          maxLength={MAX_CREATION_NAME} onChange={(e) => onCreationName(e.target.value)}
          placeholder={t(language, "cauldron.name.placeholder")} />
        <p className="creation-name__hint">{t(language, "cauldron.name.hint")}</p>
      </div>
      <div className="cauldron-actions">
        <button className={`button button--primary${busy ? " is-generating" : ""}`} type="button" onClick={onCompile} disabled={busy}>
          {t(language, busy ? "cauldron.compiling" : "cauldron.compile")}
        </button>
        {/* R69 — IT ASKS BEFORE IT EMPTIES THE POT, and the sentence is honest about what a clear
            really costs: the STACK she assembled, not the workings, which stay where she saved them.
            The first tap can only OPEN the ask; `onClear` is unreachable from the un-asked control,
            which is R35's structural law said about a text button instead of a bin icon. */}
        {clearAsk ? (
          <span className="cauldron-clear-ask" role="group" aria-label={t(language, "cauldron.clear")}>
            <span className="cauldron-clear-ask__q">{t(language, "cauldron.clearAsk")}</span>
            <button className="button button--ghost" type="button" onClick={() => onClearAsk?.(false)} disabled={busy}>{t(language, "cauldron.clearNo")}</button>
            <button className="button button--ghost cauldron-clear-ask__yes" type="button" onClick={onClear} disabled={busy}>{t(language, "cauldron.clearYes")}</button>
          </span>
        ) : (
          <button className="button button--ghost" type="button" onClick={() => onClearAsk?.(true)} disabled={busy}>{t(language, "cauldron.clear")}</button>
        )}
      </div>
    </>
  );
}

export default CauldronList;
